'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import createPanZoom, { type PanZoom } from 'panzoom';
import { drawIdeaCard, type IdeaCard } from '@concept/content';
import type { RoomView } from '@concept/protocol';
import type {
  BoardActivity,
  MarkerColor,
  MarkerKind,
  MarkerPlacement,
} from '@concept/protocol';
import {
  CATEGORY_ICONS,
  COLOR_ICONS,
  DIRECTION_ICONS,
  PROPERTY_ICONS,
  getBoardIcon,
  type BoardIcon,
} from './board-data';
import {
  availableMarkerColor,
  MARKER_COLORS,
  remainingMarkerCount,
} from './marker-inventory';
import { packMarkers } from './marker-packing';
import { constrainBoardTransform, stableDotPlaneBounds } from './board-transform';
import { useReducedImageData } from '../../hooks/use-reduced-image-data';
import {
  projectOffscreenTarget,
  spreadEdgeIndicators,
  type ViewportInsets,
} from './edge-indicators';
import {
  BOARD_INTERACTION_RULES,
  shouldActivateOnPointerUp,
} from './board-interaction-rules';
import { smallConceptIconUrl, versionedConceptAssetUrl } from './asset-url';

const BOARD_WIDTH = 1800;
const BOARD_HEIGHT = 1800;
const BOARD_ICON_SIZE = 72;
const MIN_INSPECT_ICON_SIZE = 44;
const LONG_PRESS_MS = 550;
const TOUCH_HINT_DELAY_MS = 350;
const HIGH_RESOLUTION_IDLE_DELAY_MS = 180;
const DOT_GRID_SIZE = 24;
const MARKER_CHOOSER_ESTIMATED_HEIGHT = 430;
const EDGE_INDICATOR_RADIUS = 24;
const EDGE_INDICATOR_GAP = 50;
const REQUIRE_LONG_PRESS = BOARD_INTERACTION_RULES.requireLongPress;
// Internal layout toggle: false restores the three separated action groups.
const USE_SHARED_CENTERED_BOARD_CONTROLS = true;
const PreferSmallIconImagesContext = createContext(true);

function smallIconSrc(src: string) {
  return smallConceptIconUrl(src);
}

function ConceptIconImage({
  className,
  icon,
}: {
  className?: string;
  icon: BoardIcon;
}) {
  const preferSmallImages = useContext(PreferSmallIconImagesContext);
  return (
    <img
      alt=""
      className={className}
      decoding="async"
      draggable={false}
      src={preferSmallImages ? smallIconSrc(icon.src) : icon.src}
    />
  );
}

function BoardActionIcon({
  action,
}: {
  action: 'back' | 'card' | 'clue' | 'clear' | 'overview';
}) {
  if (action === 'back') {
    return (
      <svg aria-hidden="true" className="board-action-icon" viewBox="0 0 24 24">
        <path d="m10 6-6 6 6 6" />
        <path d="M4 12h10a5 5 0 0 1 5 5v1" />
      </svg>
    );
  }

  if (action === 'card') {
    return (
      <svg aria-hidden="true" className="board-action-icon" viewBox="0 0 24 24">
        <rect height="15" rx="2.5" width="12" x="7" y="4.5" />
        <path d="M10 8h6M10 11h6M10 14h3" />
        <path d="M5.5 7.5v10a2 2 0 0 0 2 2h8" />
      </svg>
    );
  }

  if (action === 'clue') {
    return (
      <svg aria-hidden="true" className="board-action-icon" viewBox="0 0 24 24">
        <path d="M5 5.5h14v10H11l-4.5 3v-3H5z" />
        <path d="M10.1 9a2 2 0 1 1 2.5 1.95c-.6.2-.85.55-.85 1.05" />
        <path d="M11.75 13.75h.01" />
      </svg>
    );
  }

  if (action === 'clear') {
    return (
      <svg aria-hidden="true" className="board-action-icon" viewBox="0 0 24 24">
        <path d="M5 7h14M9 7V4.5h6V7M7.5 7l.8 12h7.4l.8-12" />
        <path d="M10.5 10.5v5M13.5 10.5v5" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" className="board-action-icon" viewBox="0 0 24 24">
      <path d="M3.5 12s3.2-5 8.5-5 8.5 5 8.5 5-3.2 5-8.5 5-8.5-5-8.5-5z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

function visibleViewportSize() {
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

type InspectedIcon = {
  icon: BoardIcon;
  x: number;
  y: number;
};

type MarkerChooserState = {
  icon: BoardIcon;
  left: number;
  top: number;
  color: MarkerColor;
  kind: MarkerKind | null;
  additions: MarkerDraftAddition[];
  removedMarkerIds: string[];
};

type RememberedMarkerSelection = {
  color: MarkerColor;
  kind: MarkerKind;
};

type MarkerDraftAddition = {
  key: number;
  color: MarkerColor;
  kind: MarkerKind;
};

type MarkerGroup = {
  iconId: string;
  markers: MarkerPlacement[];
};

function groupMarkersByIcon(markers: MarkerPlacement[]): MarkerGroup[] {
  const groups = new Map<string, MarkerPlacement[]>();
  [...markers]
    .sort((left, right) => left.order - right.order)
    .forEach((marker) => {
      const group = groups.get(marker.iconId) ?? [];
      group.push(marker);
      groups.set(marker.iconId, group);
    });
  return Array.from(groups, ([iconId, group]) => ({ iconId, markers: group }));
}

export type MarkerDraftChanges = {
  additions: Array<{ color: MarkerColor; kind: MarkerKind; iconId: string }>;
  removedMarkerIds: string[];
};

let nextMarkerDraftKey = 1;

function PieceArt({ color, kind }: { color: MarkerColor; kind: MarkerKind }) {
  return (
    <span aria-hidden="true" className="piece-art">
      <img
        alt=""
        draggable={false}
        src={versionedConceptAssetUrl(
          `/concept-pieces/${kind === 'pawn' ? 'marker' : 'cube'}-${color}.png`,
        )}
      />
    </span>
  );
}

export function GameBoard({
  room,
  currentPlayerId,
  leaveRoomDestination = 'room lobby',
  leaveRoomLabel = 'Exit',
  onClearBoard = () => undefined,
  onChangeNickname = () => undefined,
  onLeaveRoom = () => undefined,
  onApplyMarkerChanges,
  onMoveMarker = () => undefined,
  onPlaceMarker = () => undefined,
  onRemoveMarker = () => undefined,
  onToggleClueGiver = () => undefined,
}: {
  room: RoomView;
  currentPlayerId: string;
  leaveRoomDestination?: string;
  leaveRoomLabel?: string;
  onClearBoard?: () => void;
  onChangeNickname?: () => void;
  onLeaveRoom?: () => void;
  onApplyMarkerChanges?: (changes: MarkerDraftChanges) => void | Promise<void>;
  onMoveMarker?: (markerId: string, iconId: string) => void;
  onPlaceMarker?: (iconId: string, color: MarkerColor, kind: MarkerKind) => void;
  onRemoveMarker?: (markerId: string) => void;
  onToggleClueGiver?: () => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const boardWorldRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const dotPlaneRef = useRef<HTMLDivElement>(null);
  const edgeIndicatorLayerRef = useRef<HTMLDivElement>(null);
  const boardIconCoordinatesRef = useRef(new Map<string, { x: number; y: number }>());
  const controllerRef = useRef<PanZoom | null>(null);
  const canInspectIconsRef = useRef(false);
  const highResolutionIconsRequestedRef = useRef(false);
  const highResolutionIconsAllowedRef = useRef(false);
  const highResolutionInteractionIdleRef = useRef(true);
  const highResolutionIdleTimerRef = useRef<number | null>(null);
  const pendingHighResolutionIconIdsRef = useRef(new Set<string>());
  const imageDataPreference = useReducedImageData();
  const preferSmallIconImages = !imageDataPreference.known || imageDataPreference.reduced;
  const [inspectedIcon, setInspectedIcon] = useState<InspectedIcon | null>(null);
  const [canInspectIcons, setCanInspectIcons] = useState(false);
  const [highResolutionIconsEnabled, setHighResolutionIconsEnabled] = useState(false);
  const [highResolutionIconIds, setHighResolutionIconIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [ideaCard, setIdeaCard] = useState<IdeaCard | null>(null);
  const [isIdeaCardOpen, setIsIdeaCardOpen] = useState(false);
  const [rememberedMarkerSelection, setRememberedMarkerSelection] =
    useState<RememberedMarkerSelection>({ color: 'green', kind: 'pawn' });
  const [markerChooser, setMarkerChooser] = useState<MarkerChooserState | null>(null);
  const [movingMarkerId, setMovingMarkerId] = useState<string | null>(null);
  const [activityToasts, setActivityToasts] = useState<BoardActivity[]>([]);
  const [showBoardOverview, setShowBoardOverview] = useState(false);
  const activityRoomRef = useRef<string | null>(null);
  const lastSeenActivityIdRef = useRef(0);
  const markerGroups = groupMarkersByIcon(room.board);

  const flushPendingHighResolutionIcons = useCallback(() => {
    const pending = pendingHighResolutionIconIdsRef.current;
    if (pending.size === 0) return;
    pendingHighResolutionIconIdsRef.current = new Set();
    setHighResolutionIconIds((current) => {
      if ([...pending].every((iconId) => current.has(iconId))) return current;
      const next = new Set(current);
      pending.forEach((iconId) => next.add(iconId));
      return next;
    });
  }, []);

  const scheduleHighResolutionWork = useCallback(() => {
    highResolutionInteractionIdleRef.current = false;
    if (highResolutionIdleTimerRef.current !== null) {
      window.clearTimeout(highResolutionIdleTimerRef.current);
    }
    highResolutionIdleTimerRef.current = window.setTimeout(() => {
      highResolutionIdleTimerRef.current = null;
      highResolutionInteractionIdleRef.current = true;
      if (
        highResolutionIconsAllowedRef.current
        && canInspectIconsRef.current
        && !highResolutionIconsRequestedRef.current
      ) {
        highResolutionIconsRequestedRef.current = true;
        setHighResolutionIconsEnabled(true);
      }
      flushPendingHighResolutionIcons();
    }, HIGH_RESOLUTION_IDLE_DELAY_MS);
  }, [flushPendingHighResolutionIcons]);

  useEffect(() => () => {
    if (highResolutionIdleTimerRef.current !== null) {
      window.clearTimeout(highResolutionIdleTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const highResolutionAllowed = imageDataPreference.known && !imageDataPreference.reduced;
    highResolutionIconsAllowedRef.current = highResolutionAllowed;

    if (!highResolutionAllowed) {
      if (highResolutionIdleTimerRef.current !== null) {
        window.clearTimeout(highResolutionIdleTimerRef.current);
        highResolutionIdleTimerRef.current = null;
      }
      highResolutionInteractionIdleRef.current = true;
      pendingHighResolutionIconIdsRef.current.clear();
      highResolutionIconsRequestedRef.current = false;
      setHighResolutionIconsEnabled(false);
      setHighResolutionIconIds(new Set());
      return;
    }

    if (canInspectIconsRef.current && !highResolutionIconsRequestedRef.current) {
      scheduleHighResolutionWork();
    }
  }, [
    imageDataPreference.known,
    imageDataPreference.reduced,
    scheduleHighResolutionWork,
  ]);

  const commitMarkerChooser = useCallback(() => {
    if (markerChooser) {
      const changes: MarkerDraftChanges = {
        additions: markerChooser.additions.map(({ color, kind }) => ({
          color,
          kind,
          iconId: markerChooser.icon.id,
        })),
        removedMarkerIds: markerChooser.removedMarkerIds,
      };
      if (changes.additions.length > 0 || changes.removedMarkerIds.length > 0) {
        if (onApplyMarkerChanges) {
          void onApplyMarkerChanges(changes);
        } else {
          changes.removedMarkerIds.forEach(onRemoveMarker);
          changes.additions.forEach((addition) => {
            onPlaceMarker(addition.iconId, addition.color, addition.kind);
          });
        }
      }
      const lastAddition = markerChooser.additions.at(-1);
      if (lastAddition) {
        setRememberedMarkerSelection({
          color: lastAddition.color,
          kind: lastAddition.kind === 'pawn' ? 'cube' : lastAddition.kind,
        });
      }
    }
    setMarkerChooser(null);
  }, [markerChooser, onApplyMarkerChanges, onPlaceMarker, onRemoveMarker]);

  const openMarkerChooser = useCallback((icon: BoardIcon, x: number, y: number) => {
    const viewport = visibleViewportSize();
    const width = Math.min(340, viewport.width - 24);
    const left = Math.max(12, Math.min(viewport.width - width - 12, x - width / 2));
    const top = Math.max(
      12,
      Math.min(
        viewport.height - MARKER_CHOOSER_ESTIMATED_HEIGHT - 12,
        y - MARKER_CHOOSER_ESTIMATED_HEIGHT / 2,
      ),
    );
    const preferredKind = rememberedMarkerSelection.kind;
    const preferredColor = availableMarkerColor(
      room.board,
      rememberedMarkerSelection.color,
      preferredKind,
    );
    const fallbackKind: MarkerKind = preferredKind === 'pawn' ? 'cube' : 'pawn';
    const fallbackColor = preferredColor
      ? undefined
      : availableMarkerColor(room.board, rememberedMarkerSelection.color, fallbackKind);
    setMovingMarkerId(null);
    setMarkerChooser({
      icon,
      left,
      top,
      color: preferredColor ?? fallbackColor ?? rememberedMarkerSelection.color,
      kind: preferredColor ? preferredKind : fallbackColor ? fallbackKind : null,
      additions: [],
      removedMarkerIds: [],
    });
  }, [rememberedMarkerSelection, room.board]);

  useEffect(() => {
    if (!markerChooser) return;

    function commitOutside(event: globalThis.PointerEvent) {
      if (!(event.target as Element).closest('.marker-chooser-popover')) {
        event.preventDefault();
        event.stopPropagation();
        commitMarkerChooser();
      }
    }

    function cancelWithEscape(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') setMarkerChooser(null);
    }

    document.addEventListener('pointerdown', commitOutside, true);
    document.addEventListener('keydown', cancelWithEscape);
    return () => {
      document.removeEventListener('pointerdown', commitOutside, true);
      document.removeEventListener('keydown', cancelWithEscape);
    };
  }, [commitMarkerChooser, markerChooser]);

  useEffect(() => {
    if (movingMarkerId && !room.board.some((marker) => marker.id === movingMarkerId)) {
      setMovingMarkerId(null);
    }
  }, [movingMarkerId, room.board]);

  const inspectIcon = useCallback((icon: BoardIcon, x: number, y: number) => {
    const viewport = visibleViewportSize();
    const popoverHalfWidth = 145;
    setInspectedIcon({
      icon,
      x: Math.max(
        popoverHalfWidth + 12,
        Math.min(viewport.width - popoverHalfWidth - 12, x),
      ),
      y: Math.max(116, Math.min(viewport.height - 116, y - 20)),
    });
  }, []);

  const dismissInspectedIcon = useCallback(() => {
    setInspectedIcon(null);
  }, []);

  useEffect(() => {
    function keepPopoversInView() {
      const viewport = visibleViewportSize();
      const popoverHalfWidth = 145;

      setInspectedIcon((current) => current && ({
        ...current,
        x: Math.max(
          popoverHalfWidth + 12,
          Math.min(viewport.width - popoverHalfWidth - 12, current.x),
        ),
        y: Math.max(116, Math.min(viewport.height - 116, current.y)),
      }));

      setMarkerChooser((current) => {
        if (!current) return current;
        const width = Math.min(340, viewport.width - 24);
        return {
          ...current,
          left: Math.max(12, Math.min(viewport.width - width - 12, current.left)),
          top: Math.max(
            12,
            Math.min(
              viewport.height - MARKER_CHOOSER_ESTIMATED_HEIGHT - 12,
              current.top,
            ),
          ),
        };
      });
    }

    const visualViewport = window.visualViewport;
    window.addEventListener('resize', keepPopoversInView);
    visualViewport?.addEventListener('resize', keepPopoversInView);
    return () => {
      window.removeEventListener('resize', keepPopoversInView);
      visualViewport?.removeEventListener('resize', keepPopoversInView);
    };
  }, []);

  useEffect(() => {
    if (!inspectedIcon) return;

    function dismiss(event: globalThis.PointerEvent) {
      if (!(event.target as Element).closest('.icon-description-popover')) {
        setInspectedIcon(null);
      }
    }

    function dismissWithEscape(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') setInspectedIcon(null);
    }

    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismissWithEscape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', dismissWithEscape);
    };
  }, [inspectedIcon]);

  const fitBoard = useCallback(() => {
    const viewport = viewportRef.current;
    const controller = controllerRef.current;
    if (!viewport || !controller) return;

    const rect = viewport.getBoundingClientRect();
    const inset = Math.min(rect.width, rect.height) < 500 ? 10 : 24;
    const scale = Math.min(
      (rect.width - inset * 2) / BOARD_WIDTH,
      (rect.height - inset * 2) / BOARD_HEIGHT,
    );

    controller.setMinZoom(scale * 0.72);

    // panzoom applies bounds before zoomAbs updates its scale, which can reject
    // an initial fit when the unscaled board is larger than the viewport.
    // Set the complete transform atomically, then request a redraw.
    const transform = controller.getTransform();
    transform.scale = scale;
    transform.x = (rect.width - BOARD_WIDTH * scale) / 2;
    transform.y = (rect.height - BOARD_HEIGHT * scale) / 2;
    controller.moveBy(0, 0, false);
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('game-board-open');
    const viewport = viewportRef.current;
    const boardWorld = boardWorldRef.current;
    const board = boardRef.current;
    const dotPlane = dotPlaneRef.current;
    if (!viewport || !boardWorld || !board || !dotPlane) {
      document.documentElement.classList.remove('game-board-open');
      return;
    }
    const activeViewport = viewport;
    const activeBoardWorld = boardWorld;
    const activeBoard = board;
    const activeDotPlane = dotPlane;
    const activeIndicatorLayer = edgeIndicatorLayerRef.current;
    let lastValidTransform = { x: 0, y: 0, scale: 1 };
    let indicatorInsets: ViewportInsets = { top: 96, right: 32, bottom: 92, left: 32 };

    boardIconCoordinatesRef.current = new Map(
      Array.from(activeBoard.querySelectorAll<HTMLElement>('[data-board-icon-id]'))
        .map((element) => {
          const pieceSpace = element.querySelector<HTMLElement>('.cube-space');
          if (!pieceSpace || !element.dataset.boardIconId) return null;
          let x = pieceSpace.offsetWidth / 2;
          let y = pieceSpace.offsetHeight / 2;
          let current: HTMLElement | null = pieceSpace;
          while (current && current !== activeBoard) {
            x += current.offsetLeft;
            y += current.offsetTop;
            current = current.offsetParent as HTMLElement | null;
          }
          return [element.dataset.boardIconId, { x, y }] as const;
        })
        .filter((entry): entry is readonly [string, { x: number; y: number }] => entry !== null),
    );

    function measureIndicatorInsets() {
      const viewportRect = activeViewport.getBoundingClientRect();
      const stage = activeViewport.parentElement;
      const topHudRect = stage?.querySelector<HTMLElement>('.game-hud-top')?.getBoundingClientRect();
      const actionsRect = stage?.querySelector<HTMLElement>('.freestyle-actions')?.getBoundingClientRect();
      const controlClearance = EDGE_INDICATOR_RADIUS + 8;
      indicatorInsets = {
        top: topHudRect
          ? Math.max(controlClearance, topHudRect.bottom - viewportRect.top + controlClearance)
          : 96,
        right: controlClearance,
        bottom: actionsRect
          ? Math.max(controlClearance, viewportRect.bottom - actionsRect.top + controlClearance)
          : 92,
        left: controlClearance,
      };
    }

    function updateEdgeIndicators(
      transform: { x: number; y: number; scale: number },
      indicatorsAvailable: boolean,
      viewportSize: { width: number; height: number },
    ) {
      if (!activeIndicatorLayer) return;
      const elements = Array.from(activeIndicatorLayer.children)
        .filter((element): element is HTMLButtonElement => element instanceof HTMLButtonElement);
      if (!indicatorsAvailable) {
        elements.forEach((element) => { element.hidden = true; });
        return;
      }

      const projected = elements.flatMap((element) => {
        const iconId = element.dataset.edgeMarkerIconId;
        const coordinate = iconId ? boardIconCoordinatesRef.current.get(iconId) : undefined;
        if (!coordinate) return [];
        const target = {
          x: transform.x + coordinate.x * transform.scale,
          y: transform.y + coordinate.y * transform.scale,
        };
        const position = projectOffscreenTarget(
          target,
          viewportSize,
          indicatorInsets,
          38 * transform.scale,
        );
        return position ? [{ element, position }] : [];
      });
      const spread = spreadEdgeIndicators(
        projected.map(({ position }) => position),
        viewportSize,
        indicatorInsets,
        EDGE_INDICATOR_GAP,
      );

      const visibleElements = new Set(projected.map(({ element }) => element));
      elements.forEach((element) => { element.hidden = !visibleElements.has(element); });
      projected.forEach(({ element }, index) => {
        const position = spread[index]!;
        element.style.setProperty('--edge-indicator-x', `${position.x}px`);
        element.style.setProperty('--edge-indicator-y', `${position.y}px`);
        element.style.setProperty('--edge-indicator-angle', `${position.angle}deg`);
        element.dataset.edge = position.side;
      });
    }

    function sizeDotPlane(
      viewportSize: { width: number; height: number },
      minimumScale: number,
    ) {
      const dotBounds = stableDotPlaneBounds(
        viewportSize,
        { width: BOARD_WIDTH, height: BOARD_HEIGHT },
        minimumScale,
        DOT_GRID_SIZE,
      );
      activeDotPlane.style.left = `${dotBounds.left}px`;
      activeDotPlane.style.top = `${dotBounds.top}px`;
      activeDotPlane.style.width = `${dotBounds.width}px`;
      activeDotPlane.style.height = `${dotBounds.height}px`;
    }

    function applyBoardTransform(transform: { x: number; y: number; scale: number }) {
      scheduleHighResolutionWork();
      const rect = activeViewport.getBoundingClientRect();
      const constrained = constrainBoardTransform(
        transform,
        { width: rect.width, height: rect.height },
        { width: BOARD_WIDTH, height: BOARD_HEIGHT },
        lastValidTransform,
      );
      transform.x = constrained.x;
      transform.y = constrained.y;
      transform.scale = constrained.scale;
      lastValidTransform = constrained;

      activeBoardWorld.style.transform = `translate3d(${constrained.x}px, ${constrained.y}px, 0) scale(${constrained.scale})`;

      const isAvailable = constrained.scale * BOARD_ICON_SIZE >= MIN_INSPECT_ICON_SIZE;
      updateEdgeIndicators(constrained, isAvailable, { width: rect.width, height: rect.height });
      if (isAvailable !== canInspectIconsRef.current) {
        canInspectIconsRef.current = isAvailable;
        setCanInspectIcons(isAvailable);
        if (!isAvailable) setInspectedIcon(null);
      }

    }

    const panZoomController = {
      applyTransform: applyBoardTransform,
      getBBox: () => ({ left: 0, top: 0, width: BOARD_WIDTH, height: BOARD_HEIGHT }),
      getOwner: () => activeViewport,
    };
    const initialViewportRect = activeViewport.getBoundingClientRect();
    sizeDotPlane(
      { width: initialViewportRect.width, height: initialViewportRect.height },
      .2,
    );
    const controller = createPanZoom(activeBoardWorld, {
      autocenter: true,
      bounds: false,
      controller: panZoomController,
      maxZoom: 3.4,
      minZoom: 0.2,
      pinchSpeed: 1,
      // Trackpad pinch gestures arrive as tiny wheel deltas in desktop
      // browsers. Panzoom caps each event at 25%, so this can be deliberately
      // high without making a conventional mouse-wheel notch unmanageable.
      zoomSpeed: 4,
      smoothScroll: false,
      zoomDoubleClickSpeed: 1,
      disableKeyboardInteraction: true,
    });
    controllerRef.current = controller;

    measureIndicatorInsets();
    applyBoardTransform(controller.getTransform());

    const firstLayout = requestAnimationFrame(fitBoard);
    const resizeObserver = new ResizeObserver(() => {
      const rect = viewport.getBoundingClientRect();
      const fitScale = Math.min(
        rect.width / BOARD_WIDTH,
        rect.height / BOARD_HEIGHT,
      );
      const minimumScale = fitScale * 0.72;
      controller.setMinZoom(minimumScale);
      sizeDotPlane({ width: rect.width, height: rect.height }, minimumScale);
      measureIndicatorInsets();
      controller.moveBy(0, 0, false);
    });
    resizeObserver.observe(viewport);

    return () => {
      cancelAnimationFrame(firstLayout);
      resizeObserver.disconnect();
      controller.dispose();
      controllerRef.current = null;
      document.documentElement.classList.remove('game-board-open');
    };
  }, [fitBoard, scheduleHighResolutionWork]);

  useEffect(() => {
    const redraw = requestAnimationFrame(() => {
      controllerRef.current?.moveBy(0, 0, false);
    });
    return () => cancelAnimationFrame(redraw);
  }, [room.board]);

  useEffect(() => {
    if (!highResolutionIconsEnabled) return;
    const viewport = viewportRef.current;
    const board = boardRef.current;
    if (!viewport || !board) return;

    const observer = new IntersectionObserver((entries) => {
      const newlyVisibleIds = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => (entry.target as HTMLElement).dataset.boardIconId)
        .filter((iconId): iconId is string => Boolean(iconId));
      if (newlyVisibleIds.length === 0) return;
      newlyVisibleIds.forEach((iconId) => pendingHighResolutionIconIdsRef.current.add(iconId));
      if (highResolutionInteractionIdleRef.current) flushPendingHighResolutionIcons();
    }, {
      root: viewport,
      // Decode one icon-space ahead of the viewport without pulling in distant rows.
      rootMargin: '80px',
    });

    board.querySelectorAll<HTMLElement>('[data-board-icon-id]').forEach((element) => {
      observer.observe(element);
    });
    return () => observer.disconnect();
  }, [flushPendingHighResolutionIcons, highResolutionIconsEnabled]);

  function zoom(multiplier: number) {
    const viewport = viewportRef.current;
    const controller = controllerRef.current;
    if (!viewport || !controller) return;
    const rect = viewport.getBoundingClientRect();
    controller.smoothZoom(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
      multiplier,
    );
  }

  const focusMarkerGroup = useCallback((iconId: string) => {
    const viewport = viewportRef.current;
    const controller = controllerRef.current;
    const coordinate = boardIconCoordinatesRef.current.get(iconId);
    if (!viewport || !controller || !coordinate) return;
    const rect = viewport.getBoundingClientRect();
    const { scale } = controller.getTransform();
    controller.smoothMoveTo(
      rect.width / 2 - coordinate.x * scale,
      rect.height / 2 - coordinate.y * scale,
    );
  }, []);

  const clueGivers = room.players.filter((player) =>
    room.activeClueGiverIds?.includes(player.id),
  );
  const clueGiverNames = clueGivers.map((player) => player.nickname);
  const clueGiverStatus = clueGiverNames.length === 0
    ? 'Awaiting Clue Givers'
    : `${formatNameList(clueGiverNames)} ${clueGiverNames.length === 1 ? 'is' : 'are'} Giving Clues`;
  const amClueGiver = room.activeClueGiverIds.includes(currentPlayerId);
  const currentPlayer = room.players.find((player) => player.id === currentPlayerId);

  useEffect(() => {
    const latestId = room.boardActivity.at(-1)?.id ?? 0;
    if (activityRoomRef.current !== room.code) {
      activityRoomRef.current = room.code;
      lastSeenActivityIdRef.current = latestId;
      setActivityToasts([]);
      return;
    }

    const newActivity = room.boardActivity.filter(
      (entry) => entry.id > lastSeenActivityIdRef.current,
    );
    lastSeenActivityIdRef.current = latestId;
    if (amClueGiver || newActivity.length === 0) return;

    const clueGiverChanges = newActivity.filter((entry) => entry.actorWasClueGiver);
    if (clueGiverChanges.length > 0) {
      setActivityToasts((current) => [...current, ...clueGiverChanges].slice(-4));
    }
  }, [amClueGiver, room.boardActivity, room.code]);

  function openIdeaCard() {
    setIdeaCard((current) => current ?? drawIdeaCard());
    setIsIdeaCardOpen(true);
  }

  function drawAnotherIdeaCard() {
    setIdeaCard((current) => drawIdeaCard(current?.id));
  }

  return (
    <PreferSmallIconImagesContext.Provider value={preferSmallIconImages}>
    <main className="game-stage">
      <div
        aria-label="Concept game board. Drag to pan and pinch to zoom."
        className="board-viewport"
        ref={viewportRef}
        role="application"
      >
        <div className="board-world" ref={boardWorldRef}>
          <div aria-hidden="true" className="board-dot-plane" ref={dotPlaneRef} />
          <div
            className={`concept-board ${canInspectIcons ? 'icons-inspectable' : ''} ${
              amClueGiver ? 'markers-editable' : ''
            }`}
            ref={boardRef}
          >
          <IconGrid
            canEdit={amClueGiver}
            className="property-grid"
            canInspect={canInspectIcons}
            highResolutionIconIds={highResolutionIconIds}
            icons={PROPERTY_ICONS}
            markers={room.board}
            movingMarkerId={movingMarkerId}
            onMoveMarker={onMoveMarker}
            onOpenMarkerChooser={openMarkerChooser}
            onSelectPlacedMarker={setMovingMarkerId}
            onInspect={inspectIcon}
            onDismissInspect={dismissInspectedIcon}
            sourceColumns={4}
          />
          <IconGrid
            canEdit={amClueGiver}
            className="direction-grid"
            canInspect={canInspectIcons}
            highResolutionIconIds={highResolutionIconIds}
            icons={DIRECTION_ICONS}
            markers={room.board}
            movingMarkerId={movingMarkerId}
            onMoveMarker={onMoveMarker}
            onOpenMarkerChooser={openMarkerChooser}
            onSelectPlacedMarker={setMovingMarkerId}
            onInspect={inspectIcon}
            onDismissInspect={dismissInspectedIcon}
            sourceColumns={2}
          />
          <IconGrid
            canEdit={amClueGiver}
            className="color-grid"
            canInspect={canInspectIcons}
            highResolutionIconIds={highResolutionIconIds}
            icons={COLOR_ICONS}
            markers={room.board}
            movingMarkerId={movingMarkerId}
            onMoveMarker={onMoveMarker}
            onOpenMarkerChooser={openMarkerChooser}
            onSelectPlacedMarker={setMovingMarkerId}
            onInspect={inspectIcon}
            onDismissInspect={dismissInspectedIcon}
            pieceSpaceSide="right"
            sourceColumns={1}
          />
          <div className="board-wordmark" aria-hidden="true">
            CONCEPT
          </div>
          <IconGrid
            canEdit={amClueGiver}
            className="category-grid"
            canInspect={canInspectIcons}
            highResolutionIconIds={highResolutionIconIds}
            icons={CATEGORY_ICONS}
            markers={room.board}
            movingMarkerId={movingMarkerId}
            onMoveMarker={onMoveMarker}
            onOpenMarkerChooser={openMarkerChooser}
            onSelectPlacedMarker={setMovingMarkerId}
            onInspect={inspectIcon}
            onDismissInspect={dismissInspectedIcon}
            sourceColumns={4}
          />
          </div>
        </div>
      </div>

      <div
        aria-label="Off-screen board pieces"
        className="board-edge-indicators"
        ref={edgeIndicatorLayerRef}
      >
        {markerGroups.map(({ iconId, markers }) => {
          const icon = getBoardIcon(iconId);
          const description = markers
            .map((marker) => `${marker.color} ${marker.kind === 'pawn' ? 'marker' : 'cube'}`)
            .join(', ');
          return (
            <button
              aria-label={`${description} off screen toward ${icon?.label ?? 'a board symbol'}. Show on board.`}
              data-edge-marker-icon-id={iconId}
              hidden
              key={iconId}
              onClick={() => focusMarkerGroup(iconId)}
              type="button"
            >
              <span aria-hidden="true" className="edge-indicator-piece-stack">
                {markers.slice(0, 3).map((marker) => (
                  <i className={`edge-indicator-piece ${marker.kind} ${marker.color}`} key={marker.id}>
                    <PieceArt color={marker.color} kind={marker.kind} />
                  </i>
                ))}
              </span>
              {markers.length > 1 && <b aria-hidden="true">{markers.length}</b>}
            </button>
          );
        })}
      </div>

      {inspectedIcon && (
        <aside
          aria-live="polite"
          className="icon-description-popover"
          style={{ left: inspectedIcon.x, top: inspectedIcon.y }}
        >
          <ConceptIconImage icon={inspectedIcon.icon} />
          <strong>{inspectedIcon.icon.label}</strong>
          <button
            aria-label="Close icon description"
            onClick={() => setInspectedIcon(null)}
            type="button"
          >
            &times;
          </button>
        </aside>
      )}

      {isIdeaCardOpen && ideaCard && (
        <div
          className="idea-card-backdrop"
          onClick={(event) => {
            if (event.currentTarget === event.target) setIsIdeaCardOpen(false);
          }}
          role="presentation"
        >
          <aside
            aria-label="Concept idea card"
            aria-modal="true"
            className="idea-card-dialog"
            role="dialog"
          >
            <div className="idea-card-panel">
              {(['blue', 'red', 'black'] as const).map((tone, sectionIndex) => (
                <section className={`idea-card-level idea-card-level-${tone}`} key={tone}>
                  <span className="idea-card-face" aria-hidden="true" />
                  <ol className="idea-card-prompts" start={sectionIndex * 3 + 1}>
              {ideaCard.prompts
                      .slice(sectionIndex * 3, sectionIndex * 3 + 3)
                      .map((prompt, promptIndex) => (
                        <li className="idea-card-prompt" key={`${sectionIndex}-${promptIndex}-${prompt}`}>
                          <span aria-hidden="true">{sectionIndex * 3 + promptIndex + 1}</span>
                          <strong>{prompt}</strong>
                        </li>
                      ))}
                  </ol>
                </section>
              ))}
            </div>
            <div className="idea-card-controls">
              <button className="idea-card-draw" onClick={drawAnotherIdeaCard} type="button">
                Draw a different card
              </button>
              <button
                aria-label="Close idea card"
                className="idea-card-close"
                onClick={() => setIsIdeaCardOpen(false)}
                type="button"
              >
                &times;
              </button>
            </div>
          </aside>
        </div>
      )}

      {markerChooser && (
        <MarkerChooser
          chooser={markerChooser}
          onChange={setMarkerChooser}
          onConfirm={commitMarkerChooser}
          room={room}
        />
      )}

      <div aria-live="polite" aria-relevant="additions" className="board-toast-stack">
        {activityToasts.map((activity) => (
          <BoardActivityToast
            activity={activity}
            actorName={playerName(room, activity.actorPlayerId)}
            key={activity.id}
            onDismiss={() => {
              setActivityToasts((current) =>
                current.filter((entry) => entry.id !== activity.id),
              );
            }}
          />
        ))}
      </div>

      {showBoardOverview && (
        <BoardOverview
          canEdit={amClueGiver}
          onClose={() => setShowBoardOverview(false)}
          onInspect={inspectIcon}
          onRemoveMarker={onRemoveMarker}
          room={room}
        />
      )}

      <header className="game-hud game-hud-top">
        <div className="room-hud-controls">
          <div aria-label={`Room ${room.code}`} className="hud-pill room-pill">
            <strong>{room.code}</strong>
          </div>
          {onLeaveRoom && (
            <button
              aria-label={`${leaveRoomLabel} to ${leaveRoomDestination}`}
              className="hud-pill board-leave-button"
              onClick={onLeaveRoom}
              title={`${leaveRoomLabel} to ${leaveRoomDestination}`}
              type="button"
            >
              <strong className="mobile-room-code">{room.code}</strong>
              <BoardActionIcon action="back" />
              <span>{leaveRoomLabel}</span>
            </button>
          )}
        </div>
        <div aria-label={clueGiverStatus} className="turn-pill">
          <strong>{clueGiverStatus}</strong>
        </div>
        <button
          aria-label={`Change nickname. Current nickname: ${currentPlayer?.nickname ?? 'Guest'}`}
          className="hud-pill identity-pill"
          onClick={onChangeNickname}
          title="Change nickname"
          type="button"
        >
          <span>{currentPlayer?.nickname ?? 'Guest'}</span>
          <span aria-hidden="true" className="identity-edit">&#9998;</span>
        </button>
      </header>

      <footer className="game-hud game-hud-bottom">
        <div className="gesture-hint">
          {amClueGiver
            ? canInspectIcons
              ? REQUIRE_LONG_PRESS
                ? 'Hold a dashed space to place · Hold an icon for details'
                : 'Tap a dashed space to place · Tap an icon for details'
              : REQUIRE_LONG_PRESS
                ? 'Drag · Pinch · Hold a dashed space to place'
                : 'Drag · Pinch · Tap a dashed space to place'
            : canInspectIcons
              ? REQUIRE_LONG_PRESS
                ? 'Drag · Pinch · Hold an icon for details'
                : 'Drag · Pinch · Tap an icon for details'
              : 'Drag · Pinch · Zoom in to inspect icons'}
        </div>
        <div className="zoom-controls" aria-label="Board zoom controls">
          <button aria-label="Zoom out" onClick={() => zoom(0.8)} type="button">&minus;</button>
          <button aria-label="Fit board to screen" onClick={fitBoard} type="button">
            Fit
          </button>
          <button aria-label="Zoom in" onClick={() => zoom(1.25)} type="button">+</button>
        </div>
      </footer>

      <nav
        className={`freestyle-actions ${
          USE_SHARED_CENTERED_BOARD_CONTROLS ? 'shared-controls' : 'split-controls'
        }`}
        aria-label="Board controls"
      >
        <div className="freestyle-action-group card-controls">
          <button aria-label="Draw an idea card" onClick={openIdeaCard} title="Draw card" type="button">
            <BoardActionIcon action="card" />
            <span className="board-action-label">Card</span>
          </button>
        </div>

        <div className="freestyle-action-group overview-controls">
          <button
            aria-label="Open board overview"
            onClick={() => setShowBoardOverview(true)}
            title="Overview"
            type="button"
          >
            <BoardActionIcon action="overview" />
            <span className="board-action-label">Overview</span>
          </button>
        </div>

        <div className={`freestyle-action-group clue-controls${amClueGiver ? ' expanded' : ''}`}>
          <button
            aria-label={amClueGiver ? 'Stop giving clues' : 'Start giving clues'}
            aria-pressed={amClueGiver}
            className={amClueGiver ? 'active' : ''}
            onClick={() => onToggleClueGiver?.()}
            title={amClueGiver ? 'Stop giving clues' : 'Start giving clues'}
            type="button"
          >
            <BoardActionIcon action="clue" />
            <span className="board-action-label">{amClueGiver ? 'Stop' : 'Give Clues'}</span>
          </button>
          <button
            aria-hidden={!amClueGiver}
            aria-label="Clear board"
            className="clear-board-action"
            disabled={!amClueGiver || room.board.length === 0}
            onClick={() => {
              if (window.confirm('Clear every marker from the shared board?')) {
                onClearBoard?.();
              }
            }}
            tabIndex={amClueGiver ? 0 : -1}
            title="Clear board"
            type="button"
          >
            <BoardActionIcon action="clear" />
            <span className="board-action-label">Clear</span>
          </button>
        </div>
      </nav>

      {amClueGiver && movingMarkerId && (
        <aside className="marker-edit-toolbar" aria-label="Selected marker controls">
          <button
            onClick={() => {
              onRemoveMarker(movingMarkerId);
              setMovingMarkerId(null);
            }}
            type="button"
          >
            Remove
          </button>
          <button onClick={() => setMovingMarkerId(null)} type="button">
            Cancel
          </button>
        </aside>
      )}
    </main>
    </PreferSmallIconImagesContext.Provider>
  );
}

function MarkerChooser({
  chooser,
  onChange,
  onConfirm,
  room,
}: {
  chooser: MarkerChooserState;
  onChange: React.Dispatch<React.SetStateAction<MarkerChooserState | null>>;
  onConfirm: () => void;
  room: RoomView;
}) {
  const visibleExisting = room.board.filter(
    (marker) => marker.iconId === chooser.icon.id
      && !chooser.removedMarkerIds.includes(marker.id),
  );
  const draftBoard: RoomView['board'] = [
    ...room.board.filter((marker) => !chooser.removedMarkerIds.includes(marker.id)),
    ...chooser.additions.map((addition, index) => ({
      id: `draft-${addition.key}`,
      color: addition.color,
      kind: addition.kind,
      iconId: chooser.icon.id,
      order: room.board.length + index + 1,
    })),
  ];
  const pieceCount = visibleExisting.length + chooser.additions.length;
  const hasChanges = chooser.additions.length > 0 || chooser.removedMarkerIds.length > 0;

  function chooseKind(kind: MarkerKind | null) {
    if (kind === null) {
      onChange((current) => current ? { ...current, kind: null } : current);
      return;
    }
    const color = availableMarkerColor(draftBoard, chooser.color, kind);
    if (!color) return;
    onChange((current) => current ? { ...current, kind, color } : current);
  }

  function addSelectedPiece() {
    if (!chooser.kind) return;
    if (remainingMarkerCount(draftBoard, chooser.color, chooser.kind) <= 0) return;
    onChange((current) => {
      if (!current) return current;
      const addition: MarkerDraftAddition = {
        key: nextMarkerDraftKey++,
        color: current.color,
        kind: current.kind!,
      };
      const additions = [...current.additions, addition];
      const nextKind: MarkerKind = addition.kind === 'pawn' ? 'cube' : addition.kind;
      const nextDraftBoard: RoomView['board'] = [
        ...draftBoard,
        {
          id: `draft-${addition.key}`,
          color: addition.color,
          kind: addition.kind,
          iconId: current.icon.id,
          order: room.board.length + additions.length,
        },
      ];
      const nextColor = availableMarkerColor(nextDraftBoard, addition.color, nextKind);
      return {
        ...current,
        additions,
        color: nextColor ?? current.color,
        kind: nextColor ? nextKind : null,
      };
    });
  }

  function removeExisting(markerId: string) {
    onChange((current) => current ? {
      ...current,
      removedMarkerIds: [...current.removedMarkerIds, markerId],
    } : current);
  }

  function removeAddition(key: number) {
    onChange((current) => current ? {
      ...current,
      additions: current.additions.filter((addition) => addition.key !== key),
    } : current);
  }

  const anyPawnAvailable = MARKER_COLORS.some(
    (color) => remainingMarkerCount(draftBoard, color, 'pawn') > 0,
  );
  const anyCubeAvailable = MARKER_COLORS.some(
    (color) => remainingMarkerCount(draftBoard, color, 'cube') > 0,
  );
  const selectedRemaining = chooser.kind
    ? remainingMarkerCount(draftBoard, chooser.color, chooser.kind)
    : 0;

  return (
    <aside
      aria-label={`Choose a piece for ${chooser.icon.label}`}
      className="marker-chooser-popover"
      style={{ left: chooser.left, top: chooser.top }}
    >
      <div className={`marker-current-pieces ${pieceCount === 0 ? 'empty' : ''}`}>
        {pieceCount > 0 ? (
          <>
          <div className="marker-current-heading">
            <button
              onClick={() => {
                onChange((current) => current ? {
                  ...current,
                  additions: [],
                  removedMarkerIds: room.board
                    .filter((marker) => marker.iconId === chooser.icon.id)
                    .map((marker) => marker.id),
                } : current);
              }}
              type="button"
            >
              Clear all
            </button>
          </div>
          <div className="marker-current-tray">
            {visibleExisting.map((marker) => (
              <button
                aria-label={`Remove ${marker.color} ${marker.kind}`}
                key={marker.id}
                onClick={() => removeExisting(marker.id)}
                type="button"
              >
                <i aria-hidden="true" className={`palette-piece ${marker.kind} ${marker.color}`}>
                  <PieceArt color={marker.color} kind={marker.kind} />
                </i>
                <b aria-hidden="true">&times;</b>
              </button>
            ))}
            {chooser.additions.map((addition) => (
              <button
                aria-label={`Remove staged ${addition.color} ${addition.kind}`}
                className="pending"
                key={addition.key}
                onClick={() => removeAddition(addition.key)}
                type="button"
              >
                <i aria-hidden="true" className={`palette-piece ${addition.kind} ${addition.color}`}>
                  <PieceArt color={addition.color} kind={addition.kind} />
                </i>
                <b aria-hidden="true">&times;</b>
              </button>
            ))}
          </div>
          </>
        ) : (
          <p className="marker-current-empty">No pieces placed</p>
        )}
      </div>

      <div aria-label="Piece type" className="marker-kind-options" role="group">
        <button
          aria-label="Marker"
          aria-pressed={chooser.kind === 'pawn'}
          className={chooser.kind === 'pawn' ? 'selected' : ''}
          disabled={!anyPawnAvailable}
          onClick={() => chooseKind('pawn')}
          type="button"
        >
          <i aria-hidden="true" className={`palette-piece pawn ${chooser.color}`}>
            <PieceArt color={chooser.color} kind="pawn" />
          </i>
        </button>
        <button
          aria-label="Cube"
          aria-pressed={chooser.kind === 'cube'}
          className={chooser.kind === 'cube' ? 'selected' : ''}
          disabled={!anyCubeAvailable}
          onClick={() => chooseKind('cube')}
          type="button"
        >
          <i aria-hidden="true" className={`palette-piece cube ${chooser.color}`}>
            <PieceArt color={chooser.color} kind="cube" />
          </i>
        </button>
      </div>

      {chooser.kind && (
        <div aria-label="Piece color" className="marker-color-options" role="group">
          {MARKER_COLORS.map((color) => {
            const remaining = remainingMarkerCount(draftBoard, color, chooser.kind!);
            return (
              <button
                aria-label={Number.isFinite(remaining)
                  ? `${color}, ${remaining} remaining`
                  : `${color}, unlimited`}
                aria-pressed={chooser.color === color}
                className={`${color} ${chooser.color === color ? 'selected' : ''}`}
                disabled={remaining === 0}
                key={color}
                onClick={() => {
                  onChange((current) => current ? { ...current, color } : current);
                }}
                type="button"
              >
                <i
                  aria-hidden="true"
                  className={`palette-piece ${chooser.kind} ${color}`}
                >
                  <PieceArt color={color} kind={chooser.kind!} />
                </i>
                <small>{Number.isFinite(remaining) ? remaining : '∞'}</small>
              </button>
            );
          })}
        </div>
      )}

      <div className="marker-chooser-actions">
        <span aria-hidden="true" className="marker-chooser-context-icon">
          <ConceptIconImage icon={chooser.icon} />
        </span>
        <button
          aria-label="Add selected piece"
          className="marker-add-piece"
          disabled={!chooser.kind || selectedRemaining <= 0}
          onClick={addSelectedPiece}
          type="button"
        >
          <span aria-hidden="true">+</span>
        </button>
        <button
          aria-label={hasChanges ? 'Apply piece changes' : 'Close piece editor'}
          className={`marker-chooser-confirm ${hasChanges ? 'changed' : ''}`}
          onClick={onConfirm}
          type="button"
        >
          &#10003;
        </button>
      </div>
    </aside>
  );
}

function InspectableOverviewIcon({
  icon,
  onInspect,
}: {
  icon: BoardIcon;
  onInspect: (icon: BoardIcon, x: number, y: number) => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef({ x: 0, y: 0 });
  const [pressing, setPressing] = useState(false);

  function cancelLongPress() {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPressing(false);
  }

  useEffect(() => () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
  }, []);

  function startLongPress(event: PointerEvent<HTMLSpanElement>) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    cancelLongPress();
    const point = { x: event.clientX, y: event.clientY };
    startRef.current = point;
    if (!REQUIRE_LONG_PRESS) return;
    setPressing(true);
    timerRef.current = setTimeout(() => {
      onInspect(icon, point.x, point.y);
      timerRef.current = null;
      setPressing(false);
    }, LONG_PRESS_MS);
  }

  function trackLongPress(event: PointerEvent<HTMLSpanElement>) {
    if (timerRef.current === null) return;
    if (Math.hypot(
      event.clientX - startRef.current.x,
      event.clientY - startRef.current.y,
    ) > 8) cancelLongPress();
  }

  function inspectWithKeyboard(event: KeyboardEvent<HTMLSpanElement>) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onInspect(icon, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  function inspectWithClick(event: MouseEvent<HTMLSpanElement>) {
    if (REQUIRE_LONG_PRESS) return;
    onInspect(icon, event.clientX, event.clientY);
  }

  return (
    <span
      aria-label={`Symbol. ${REQUIRE_LONG_PRESS ? 'Long press' : 'Press'} for its hint.`}
      className={`overview-inspect-icon${pressing ? ' icon-long-press-pending' : ''}`}
      onClick={inspectWithClick}
      onContextMenu={(event) => event.preventDefault()}
      onKeyDown={inspectWithKeyboard}
      onPointerCancel={cancelLongPress}
      onPointerDown={startLongPress}
      onPointerLeave={cancelLongPress}
      onPointerMove={trackLongPress}
      onPointerUp={cancelLongPress}
      role="button"
      tabIndex={0}
    >
      <ConceptIconImage className="activity-icon" icon={icon} />
    </span>
  );
}

function BoardActivityToast({
  activity,
  actorName,
  onDismiss,
}: {
  activity: BoardActivity;
  actorName: string;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, 4600);
    return () => window.clearTimeout(timer);
  }, [activity.id, onDismiss]);

  const icon = activity.marker ? getBoardIcon(activity.marker.iconId) : undefined;
  const actionLabel = activity.action === 'added'
    ? 'added'
    : activity.action === 'removed'
      ? 'removed'
      : activity.action === 'moved'
        ? 'moved'
        : 'cleared the board';

  return (
    <aside className="board-activity-toast">
      <ActivityPiece action={activity.action} marker={activity.marker} />
      {icon && <ConceptIconImage className="activity-icon" icon={icon} />}
      <div>
        <strong>{actorName}</strong>
        <span>
          {activity.marker
            ? `${actionLabel} a ${activity.marker.color} ${activity.marker.kind}`
            : actionLabel}
        </span>
      </div>
      <button aria-label="Dismiss notification" onClick={onDismiss} type="button">
        &times;
      </button>
    </aside>
  );
}

function BoardOverview({
  canEdit,
  onClose,
  onInspect,
  onRemoveMarker,
  room,
}: {
  canEdit: boolean;
  onClose: () => void;
  onInspect: (icon: BoardIcon, x: number, y: number) => void;
  onRemoveMarker: (markerId: string) => void;
  room: RoomView;
}) {
  const [view, setView] = useState<'concepts' | 'history'>('concepts');
  const [showFullHistory, setShowFullHistory] = useState(false);
  const activeConceptCount = new Set(room.board.map((marker) => marker.color)).size;
  const items = showFullHistory
    ? room.boardActivity.map((activity) => ({
        key: `activity-${activity.id}`,
        activity,
        actorPlayerId: activity.actorPlayerId,
      }))
    : [...room.board]
        .sort((left, right) => left.order - right.order)
        .map((marker) => {
          const latestActivity = [...room.boardActivity].reverse().find(
            (activity) => activity.marker?.id === marker.id,
          );
          return {
            key: `marker-${marker.id}`,
            activity: {
              id: marker.order,
              action: 'added' as const,
              actorPlayerId: latestActivity?.actorPlayerId ?? room.hostPlayerId,
              actorWasClueGiver: true,
              marker,
              previousIconId: null,
              at: latestActivity?.at ?? 0,
            },
            actorPlayerId: latestActivity?.actorPlayerId ?? null,
          };
        });

  return (
    <div className="board-overview-backdrop" role="presentation">
      <section
        aria-label="Board activity overview"
        aria-modal="true"
        className="board-overview"
        role="dialog"
      >
        <header>
          <strong>Board overview</strong>
          <button aria-label="Close board overview" onClick={onClose} type="button">
            &times;
          </button>
        </header>
        <div aria-label="Overview display" className="board-overview-toggle" role="group">
          <button
            aria-pressed={view === 'concepts'}
            className={view === 'concepts' ? 'active' : ''}
            onClick={() => setView('concepts')}
            type="button"
          >
            Concepts
            <small>{activeConceptCount}</small>
          </button>
          <button
            aria-pressed={view === 'history'}
            className={view === 'history' ? 'active' : ''}
            onClick={() => setView('history')}
            type="button"
          >
            History
            <small>{showFullHistory ? room.boardActivity.length : room.board.length}</small>
          </button>
        </div>
        <div className={`board-overview-content ${view}`}>
          {view === 'history' && (
            <label className="history-scope-toggle">
              <strong>Show full history</strong>
              <input
                checked={showFullHistory}
                onChange={(event) => setShowFullHistory(event.target.checked)}
                role="switch"
                type="checkbox"
              />
            </label>
          )}
          {view === 'concepts' ? (
            <ConceptGroups
              canEdit={canEdit}
              onInspect={onInspect}
              onRemoveMarker={onRemoveMarker}
              room={room}
            />
          ) : items.length === 0 ? (
            <p className="board-overview-empty">
              {showFullHistory ? 'No board changes yet.' : 'The board is currently empty.'}
            </p>
          ) : (
            <ol className="board-overview-list">
              {items.map(({ activity, actorPlayerId, key }, index) => {
                const icon = activity.marker ? getBoardIcon(activity.marker.iconId) : undefined;
                const actor = actorPlayerId ? playerName(room, actorPlayerId) : null;
                const description = !showFullHistory
                  ? actor ? `Active · last changed by ${actor}` : 'Active marker'
                  : activity.action === 'cleared'
                    ? `Board cleared by ${actor ?? 'a player'}`
                    : `${capitalize(activity.action)} by ${actor ?? 'a player'}`;
                return (
                  <li key={key}>
                    <span className="activity-sequence">{index + 1}</span>
                    <ActivityPiece action={activity.action} marker={activity.marker} />
                    {icon
                      ? <InspectableOverviewIcon icon={icon} onInspect={onInspect} />
                      : <span aria-hidden="true" className="activity-icon activity-icon-empty">×</span>}
                    <div>
                      <strong>{activity.action === 'cleared' ? 'Board cleared' : description}</strong>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}

function ConceptGroups({
  canEdit,
  onInspect,
  onRemoveMarker,
  room,
}: {
  canEdit: boolean;
  onInspect: (icon: BoardIcon, x: number, y: number) => void;
  onRemoveMarker: (markerId: string) => void;
  room: RoomView;
}) {
  const groups = MARKER_COLORS.map((color) => ({
    color,
    markers: room.board
      .filter((marker) => marker.color === color)
      .sort((left, right) => {
        if (left.kind !== right.kind) return left.kind === 'pawn' ? -1 : 1;
        return left.order - right.order;
      }),
  })).filter((group) => group.markers.length > 0);

  if (groups.length === 0) {
    return <p className="board-overview-empty">No active concepts yet.</p>;
  }

  return (
    <div className="concept-overview-groups">
      {groups.map(({ color, markers }) => {
        const conceptMarker = markers.find((marker) => marker.kind === 'pawn');
        const conceptIcon = conceptMarker ? getBoardIcon(conceptMarker.iconId) : undefined;
        const subConceptMarkers = markers.filter((marker) => marker.kind === 'cube');

        return (
          <section className={`concept-overview-group ${color}`} key={color}>
            <header className={conceptMarker ? '' : 'without-concept-marker'}>
              <strong>{capitalize(color)} concept</strong>
              {conceptMarker && (
                <>
                  <i
                    aria-hidden="true"
                    className={`placed-marker ${conceptMarker.kind} ${conceptMarker.color}`}
                  >
                    <PieceArt color={conceptMarker.color} kind={conceptMarker.kind} />
                  </i>
                  {conceptIcon && (
                    <InspectableOverviewIcon icon={conceptIcon} onInspect={onInspect} />
                  )}
                  {canEdit && (
                    <button
                      aria-label={`Remove ${conceptMarker.color} marker from ${conceptIcon?.label ?? 'this symbol'}`}
                      className="concept-remove-button"
                      onClick={() => onRemoveMarker(conceptMarker.id)}
                      title="Remove from board"
                      type="button"
                    >
                      &minus;
                    </button>
                  )}
                </>
              )}
            </header>
            {subConceptMarkers.length > 0 && (
              <ol>
                {subConceptMarkers.map((marker) => {
                  const icon = getBoardIcon(marker.iconId);
                  return (
                    <li
                      className={`cube-clue ${canEdit ? 'editable' : ''}`}
                      key={marker.id}
                    >
                      <i
                        aria-hidden="true"
                        className={`placed-marker ${marker.kind} ${marker.color}`}
                      >
                        <PieceArt color={marker.color} kind={marker.kind} />
                      </i>
                      {icon && <InspectableOverviewIcon icon={icon} onInspect={onInspect} />}
                      {canEdit && (
                        <button
                          aria-label={`Remove ${marker.color} ${marker.kind} from ${icon?.label ?? 'this symbol'}`}
                          className="concept-remove-button"
                          onClick={() => onRemoveMarker(marker.id)}
                          title="Remove from board"
                          type="button"
                        >
                          &minus;
                        </button>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        );
      })}
    </div>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function ActivityPiece({
  action,
  marker,
}: {
  action: BoardActivity['action'];
  marker: MarkerPlacement | null;
}) {
  const sign = action === 'added' ? '+' : action === 'removed' ? '−' : action === 'moved' ? '→' : '×';
  return (
    <span className={`activity-piece action-${action}`}>
      <b>{sign}</b>
      {marker && (
        <i
          aria-hidden="true"
          className={`placed-marker ${marker.kind} ${marker.color}`}
        >
          <PieceArt color={marker.color} kind={marker.kind} />
        </i>
      )}
    </span>
  );
}

function playerName(room: RoomView, playerId: string): string {
  return room.players.find((player) => player.id === playerId)?.nickname ?? 'A clue giver';
}

function formatNameList(names: string[]): string {
  if (names.length < 2) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`;
}

function IconGrid({
  canEdit,
  canInspect,
  className,
  highResolutionIconIds,
  icons,
  markers,
  movingMarkerId,
  onMoveMarker,
  onOpenMarkerChooser,
  onSelectPlacedMarker,
  onInspect,
  onDismissInspect,
  pieceSpaceSide,
  sourceColumns,
}: {
  canEdit: boolean;
  canInspect: boolean;
  className: string;
  highResolutionIconIds: ReadonlySet<string>;
  icons: BoardIcon[];
  markers: MarkerPlacement[];
  movingMarkerId: string | null;
  onMoveMarker: (markerId: string, iconId: string) => void;
  onOpenMarkerChooser: (icon: BoardIcon, x: number, y: number) => void;
  onSelectPlacedMarker: (markerId: string | null) => void;
  onInspect: (icon: BoardIcon, x: number, y: number) => void;
  onDismissInspect: () => void;
  pieceSpaceSide?: 'left' | 'right';
  sourceColumns: number;
}) {
  return (
    <div className={`board-icon-grid ${className}`}>
      {icons.map((icon, index) => (
        <IconSpace
          canEdit={canEdit}
          canInspect={canInspect}
          className={`board-icon-space ${
            pieceSpaceSide === 'left' ||
            (pieceSpaceSide === undefined && index % 2 === 0)
              ? 'cube-space-left'
              : 'cube-space-right'
          }`}
          icon={icon}
          highResolution={highResolutionIconIds.has(icon.id)}
          key={icon.id}
          markers={markers.filter((marker) => marker.iconId === icon.id)}
          movingMarkerId={movingMarkerId}
          onInspect={onInspect}
          onDismissInspect={onDismissInspect}
          onMoveMarker={onMoveMarker}
          onOpenMarkerChooser={onOpenMarkerChooser}
          onSelectPlacedMarker={onSelectPlacedMarker}
          style={{
            gridColumn: (index % sourceColumns) + 1,
            gridRow: Math.floor(index / sourceColumns) + 1,
          }}
        />
      ))}
    </div>
  );
}

function ProgressiveBoardIcon({
  highResolution,
  icon,
}: {
  highResolution: boolean;
  icon: BoardIcon;
}) {
  const preferSmallImages = useContext(PreferSmallIconImagesContext);
  const [src, setSrc] = useState(() => smallIconSrc(icon.src));
  const upgradedRef = useRef(false);

  useEffect(() => {
    if (!highResolution || preferSmallImages) {
      upgradedRef.current = false;
      setSrc(smallIconSrc(icon.src));
      return;
    }
    if (upgradedRef.current) return;
    let cancelled = false;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.onerror = () => undefined;
    image.onload = () => {
      const showFullResolution = () => {
        if (cancelled) return;
        upgradedRef.current = true;
        setSrc(icon.src);
      };

      if (typeof image.decode === 'function') {
        void image.decode().catch(() => undefined).finally(showFullResolution);
      } else {
        showFullResolution();
      }
    };
    image.src = icon.src;

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [highResolution, icon.src, preferSmallImages]);

  return (
    <img
      alt=""
      data-icon-resolution={upgradedRef.current ? 'full' : 'small'}
      decoding="async"
      draggable={false}
      loading="eager"
      src={src}
    />
  );
}

function IconSpace({
  canEdit,
  canInspect,
  className,
  highResolution,
  icon,
  markers,
  movingMarkerId,
  onInspect,
  onDismissInspect,
  onMoveMarker,
  onOpenMarkerChooser,
  onSelectPlacedMarker,
  style,
}: {
  canEdit: boolean;
  canInspect: boolean;
  className: string;
  highResolution: boolean;
  icon: BoardIcon;
  markers: MarkerPlacement[];
  movingMarkerId: string | null;
  onInspect: (icon: BoardIcon, x: number, y: number) => void;
  onDismissInspect: () => void;
  onMoveMarker: (markerId: string, iconId: string) => void;
  onOpenMarkerChooser: (icon: BoardIcon, x: number, y: number) => void;
  onSelectPlacedMarker: (markerId: string | null) => void;
  style: React.CSSProperties;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const longPressedRef = useRef(false);
  const markerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markerStartRef = useRef({ x: 0, y: 0 });
  const markerAnchorRef = useRef({ x: 0, y: 0 });
  const markerMovedRef = useRef(false);
  const markerLongPressedRef = useRef(false);
  const activePointerTargetRef = useRef<'icon' | 'marker' | null>(null);
  const directActivationRef = useRef<{ time: number } | null>(null);
  const touchHintVisibleRef = useRef(false);
  const [iconPressing, setIconPressing] = useState(false);
  const [markerSpacePressing, setMarkerSpacePressing] = useState(false);
  const sortedMarkers = [...markers].sort((left, right) => left.order - right.order);
  const markerPositions = packMarkers(
    sortedMarkers.length,
    sortedMarkers.map((marker) => marker.kind),
  );

  function cancelLongPress() {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    setIconPressing(false);
  }

  function cancelMarkerLongPress() {
    if (markerTimerRef.current !== null) clearTimeout(markerTimerRef.current);
    markerTimerRef.current = null;
    setMarkerSpacePressing(false);
  }

  function dismissTouchHint() {
    if (!touchHintVisibleRef.current) return;
    touchHintVisibleRef.current = false;
    onDismissInspect();
  }

  useEffect(() => {
    if (!canInspect) {
      cancelLongPress();
      dismissTouchHint();
    }
  }, [canInspect]);

  useEffect(() => {
    if (!canEdit) cancelMarkerLongPress();
    return cancelMarkerLongPress;
  }, [canEdit]);

  function startLongPress(event: PointerEvent<HTMLDivElement>) {
    if (!canInspect) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    cancelLongPress();
    movedRef.current = false;
    longPressedRef.current = false;
    startRef.current = { x: event.clientX, y: event.clientY };
    if (!REQUIRE_LONG_PRESS) {
      if (event.pointerType !== 'mouse') {
        const { clientX, clientY } = event;
        timerRef.current = setTimeout(() => {
          touchHintVisibleRef.current = true;
          onInspect(icon, clientX, clientY);
          timerRef.current = null;
        }, TOUCH_HINT_DELAY_MS);
      }
      return;
    }
    setIconPressing(true);
    timerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      setIconPressing(false);
      onInspect(icon, event.clientX, event.clientY);
      timerRef.current = null;
    }, LONG_PRESS_MS);
  }

  function trackLongPress(event: PointerEvent<HTMLDivElement>) {
    const dx = event.clientX - startRef.current.x;
    const dy = event.clientY - startRef.current.y;
    if (!REQUIRE_LONG_PRESS) {
      if (Math.hypot(dx, dy) > 8) {
        movedRef.current = true;
        cancelLongPress();
        dismissTouchHint();
      }
      return;
    }
    if (timerRef.current === null) return;
    if (Math.hypot(dx, dy) > 8) cancelLongPress();
    if (Math.hypot(dx, dy) > 8) movedRef.current = true;
  }

  function startMarkerLongPress(event: PointerEvent<HTMLDivElement>) {
    if (!canEdit) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    cancelMarkerLongPress();
    markerMovedRef.current = false;
    markerLongPressedRef.current = false;
    markerStartRef.current = { x: event.clientX, y: event.clientY };
    const pieceSpace = event.currentTarget.querySelector<HTMLElement>('.cube-space');
    const pieceSpaceRect = pieceSpace?.getBoundingClientRect();
    markerAnchorRef.current = pieceSpaceRect
      ? {
          x: pieceSpaceRect.left + pieceSpaceRect.width / 2,
          y: pieceSpaceRect.top + pieceSpaceRect.height / 2,
        }
      : markerStartRef.current;
    if (!REQUIRE_LONG_PRESS) return;
    setMarkerSpacePressing(true);
    markerTimerRef.current = setTimeout(() => {
      markerLongPressedRef.current = true;
      setMarkerSpacePressing(false);
      onOpenMarkerChooser(icon, markerAnchorRef.current.x, markerAnchorRef.current.y);
      markerTimerRef.current = null;
    }, LONG_PRESS_MS);
  }

  function trackMarkerLongPress(event: PointerEvent<HTMLDivElement>) {
    const distance = Math.hypot(
      event.clientX - markerStartRef.current.x,
      event.clientY - markerStartRef.current.y,
    );
    if (!REQUIRE_LONG_PRESS) {
      if (distance > 8) markerMovedRef.current = true;
      return;
    }
    if (markerTimerRef.current === null) return;
    if (distance > 8) {
      markerMovedRef.current = true;
      cancelMarkerLongPress();
    }
  }

  function openMarkerChooserWithKeyboard(event: KeyboardEvent<HTMLSpanElement>) {
    if (!canEdit || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onOpenMarkerChooser(icon, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  function inspectWithKeyboard(event: KeyboardEvent<HTMLDivElement>) {
    if (!canInspect) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    onInspect(icon, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  function inspectWithClick(event: MouseEvent<HTMLDivElement>) {
    if (REQUIRE_LONG_PRESS || !canInspect) return;
    if (consumeDirectActivation()) return;
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    const target = event.target;
    if (target instanceof Element && target.closest('.cube-space')) return;
    onInspect(icon, event.clientX, event.clientY);
  }

  function pointerStartedInPieceSpace(event: PointerEvent<HTMLDivElement>) {
    const target = event.target;
    if (target instanceof Element && target.closest('.cube-space')) return true;

    const pieceSpace = event.currentTarget.querySelector<HTMLElement>('.cube-space');
    if (!pieceSpace) return false;
    const rect = pieceSpace.getBoundingClientRect();
    return event.clientX >= rect.left
      && event.clientX <= rect.right
      && event.clientY >= rect.top
      && event.clientY <= rect.bottom;
  }

  function startSpaceInteraction(event: PointerEvent<HTMLDivElement>) {
    if (pointerStartedInPieceSpace(event)) {
      activePointerTargetRef.current = 'marker';
      cancelLongPress();
      if (canEdit) startMarkerLongPress(event);
      return;
    }
    activePointerTargetRef.current = 'icon';
    cancelMarkerLongPress();
    startLongPress(event);
  }

  function trackSpaceInteraction(event: PointerEvent<HTMLDivElement>) {
    trackLongPress(event);
    trackMarkerLongPress(event);
  }

  function consumeDirectActivation() {
    const activation = directActivationRef.current;
    if (!activation) return false;
    directActivationRef.current = null;
    // A compatibility click may be retargeted after opening a popover. Any
    // click immediately following a direct touch activation is the same tap.
    return performance.now() - activation.time < 450;
  }

  function finishSpaceInteraction(event: PointerEvent<HTMLDivElement>) {
    const target = activePointerTargetRef.current;
    const moved = target === 'marker' ? markerMovedRef.current : movedRef.current;
    const wasTouchHintGesture = target === 'icon'
      && !REQUIRE_LONG_PRESS
      && event.pointerType !== 'mouse';
    cancelLongPress();
    cancelMarkerLongPress();
    activePointerTargetRef.current = null;

    if (wasTouchHintGesture) {
      directActivationRef.current = { time: performance.now() };
      dismissTouchHint();
      return;
    }

    if (!target || !shouldActivateOnPointerUp({
      moved,
      pointerType: event.pointerType,
      requireLongPress: REQUIRE_LONG_PRESS,
    })) return;

    directActivationRef.current = { time: performance.now() };
    if (target === 'marker') {
      if (!canEdit) return;
      if (movingMarkerId) {
        onMoveMarker(movingMarkerId, icon.id);
        onSelectPlacedMarker(null);
      } else {
        onOpenMarkerChooser(icon, markerAnchorRef.current.x, markerAnchorRef.current.y);
      }
      return;
    }

    if (canInspect) onInspect(icon, event.clientX, event.clientY);
  }

  function cancelSpaceInteraction() {
    cancelLongPress();
    cancelMarkerLongPress();
    dismissTouchHint();
    activePointerTargetRef.current = null;
  }

  return (
    <div
      aria-disabled={!canInspect}
      aria-label={
        canInspect
          ? `${icon.label}. ${REQUIRE_LONG_PRESS ? 'Long press' : 'Press'} for description.`
          : 'Zoom in to inspect this icon.'
      }
      className={`${className}${iconPressing ? ' icon-long-press-pending' : ''}`}
      data-board-icon-id={icon.id}
      onClick={inspectWithClick}
      onContextMenu={(event) => event.preventDefault()}
      onKeyDown={inspectWithKeyboard}
      onPointerCancel={cancelSpaceInteraction}
      onPointerDown={startSpaceInteraction}
      onPointerLeave={cancelSpaceInteraction}
      onPointerMove={trackSpaceInteraction}
      onPointerUp={finishSpaceInteraction}
      role="button"
      style={style}
      tabIndex={canInspect ? 0 : -1}
    >
      <span aria-hidden="true" className="icon-art">
        <ProgressiveBoardIcon highResolution={highResolution} icon={icon} />
      </span>
      <span
        aria-label={`Piece space for ${icon.label}. ${REQUIRE_LONG_PRESS ? 'Hold' : 'Press'} to choose a piece.`}
        className={`cube-space${markerSpacePressing ? ' marker-long-press-pending' : ''}`}
        onKeyDown={openMarkerChooserWithKeyboard}
        onClick={(event) => {
          event.stopPropagation();
          if (consumeDirectActivation()) return;
          if (!canEdit || markerMovedRef.current || markerLongPressedRef.current) {
            markerLongPressedRef.current = false;
            markerMovedRef.current = false;
            return;
          }
          if (movingMarkerId) {
            onMoveMarker(movingMarkerId, icon.id);
            onSelectPlacedMarker(null);
          } else if (!REQUIRE_LONG_PRESS) {
            const rect = event.currentTarget.getBoundingClientRect();
            onOpenMarkerChooser(icon, rect.left + rect.width / 2, rect.top + rect.height / 2);
          }
        }}
        role="button"
        tabIndex={canEdit ? 0 : -1}
      >
        <span className="piece-space-visual">
          {canEdit && canInspect && sortedMarkers.length === 0 && (
            <span aria-hidden="true" className="empty-piece-space-plus">+</span>
          )}
          {sortedMarkers.map((marker, index) => {
            const position = markerPositions[index]!;
            return (
              <button
                aria-label={`Select ${marker.color} ${marker.kind} to move or remove`}
                className={`placed-marker packed-marker ${marker.kind} ${marker.color} ${
                  movingMarkerId === marker.id ? 'selected' : ''
                }`}
                disabled={!canEdit}
                key={marker.id}
                onClick={(event) => {
                  event.stopPropagation();
                  if (markerLongPressedRef.current) {
                    markerLongPressedRef.current = false;
                    return;
                  }
                  if (canEdit) onSelectPlacedMarker(marker.id);
                }}
                style={
                  {
                    '--packed-left': `${position.left}%`,
                    '--packed-top': `${position.top}%`,
                    '--packed-size': `${position.size}%`,
                  } as React.CSSProperties
                }
                title={`#${marker.order}`}
                type="button"
              >
                <PieceArt color={marker.color} kind={marker.kind} />
              </button>
            );
          })}
        </span>
      </span>
    </div>
  );
}
